//© 2025 Kaitlyn Troxell
import React, { useEffect, useRef, useState } from "react";
import { Draggable } from "@fullcalendar/interaction";
import ProjectManager from "./ProjectManager";
import { exportTasksToExcel } from '../utils/taskExport';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

function TasksSidebar({ 
  tasks, 
  loading, 
  error, 
  phaseFilter, 
  setPhaseFilter, 
  contractors,
  setSelectedTask,
  currentProject,
  projects,
  setCurrentProjectId,
  createProject,
  deleteProject,
  events,
  setTasks,
  currentUser,  // Add currentUser prop
  isAdmin  // Add isAdmin prop
}) {
  const externalRef = useRef(null);
  const [showProjectManager, setShowProjectManager] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [addTaskLoading, setAddTaskLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    phase: '',
    addTo: 'current' // 'current', 'all', or 'template'
  });
  const [formError, setFormError] = useState('');

  // Check if current user is authorized to add tasks
  const canAddTasks = currentUser?.email === 'katiegrace0521@gmail.com';
  
  // Admin users can only add to current project
  const showAddTaskOptions = canAddTasks;

  // Make sidebar items draggable
  useEffect(() => {
    if (externalRef.current && tasks.length > 0) {
      new Draggable(externalRef.current, {
        itemSelector: ".fc-event",
        eventData: (eventEl) => {
          return {
            title: eventEl.textContent.replace(" ✓", "")
          };
        },
      });
    }
  }, [tasks]);

  // Filter tasks by search query
  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  // Group tasks by phase for better organization
  const groupedTasks = filteredTasks.reduce((acc, task) => {
    const phase = task.phase || "Other";
    if (!acc[phase]) {
      acc[phase] = [];
    }
    acc[phase].push(task);
    return acc;
  }, {});

  // Get all unique phases from tasks
  const allPhases = [...new Set(tasks.map(t => t.phase || "Other"))].sort();

  const handleAddTaskChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFormError('');
  };

  const handleAddTaskSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      setFormError('Task title is required');
      return;
    }

    if (!formData.phase) {
      setFormError('Please select a phase');
      return;
    }

    if (formData.addTo === 'current' && (!currentProject || !currentProject.id)) {
      setFormError('No project selected. Please select a project first.');
      return;
    }

    setAddTaskLoading(true);
    setFormError('');

    try {
      // Get the highest order number in the current phase
      const tasksInPhase = tasks.filter(t => t.phase === formData.phase);
      const maxOrder = tasksInPhase.length > 0 
        ? Math.max(...tasksInPhase.map(t => t.order || 0)) 
        : 0;

      const baseTaskData = {
        title: formData.title.trim(),
        phase: formData.phase,
        order: maxOrder + 1,
        phase_order: 0,
      };

      // If user is admin, force addTo to be 'current'
      const effectiveAddTo = isAdmin ? 'current' : formData.addTo;

      if (effectiveAddTo === 'current') {
        // Add to current project only
        const newTask = {
          ...baseTaskData,
          contractor: null,
          completed: false,
          date_scheduled: null,
          date_completed: null,
          notes: null,
          project_id: currentProject.id,
          created_at: serverTimestamp()
        };

        const docRef = await addDoc(collection(db, 'tasks'), newTask);

        // Update local state
        setTasks(prev => [...prev, {
          id: docRef.id,
          ...newTask,
          created_at: new Date()
        }]);

        alert('Task added to current project successfully!');

      } else if (effectiveAddTo === 'all') {
        // Add to all existing projects
        if (!projects || projects.length === 0) {
          setFormError('No projects found');
          return;
        }

        const addPromises = projects.map(project => 
          addDoc(collection(db, 'tasks'), {
            ...baseTaskData,
            contractor: null,
            completed: false,
            date_scheduled: null,
            date_completed: null,
            notes: null,
            project_id: project.id,
            created_at: serverTimestamp()
          })
        );

        await Promise.all(addPromises);

        // If current project exists, refresh its tasks
        if (currentProject) {
          const currentProjectTaskRef = await addDoc(collection(db, 'tasks'), {
            ...baseTaskData,
            contractor: null,
            completed: false,
            date_scheduled: null,
            date_completed: null,
            notes: null,
            project_id: currentProject.id,
            created_at: serverTimestamp()
          });

          setTasks(prev => [...prev, {
            id: currentProjectTaskRef.id,
            ...baseTaskData,
            contractor: null,
            completed: false,
            date_scheduled: null,
            date_completed: null,
            notes: null,
            project_id: currentProject.id,
            created_at: new Date()
          }]);
        }

        alert(`Task added to all ${projects.length} existing projects successfully!`);

      } else if (effectiveAddTo === 'template') {
        // Add to task template
        const templateTask = {
          ...baseTaskData,
        };

        await addDoc(collection(db, 'task_templates'), templateTask);

        alert('Task added to template successfully! It will appear in all new projects.');
      }

      // Reset form and close modal
      setFormData({ title: '', phase: '', addTo: 'current' });
      setShowAddTaskModal(false);
      
    } catch (error) {
      console.error('Error adding task:', error);
      setFormError('Failed to add task. Please try again.');
    } finally {
      setAddTaskLoading(false);
    }
  };

  return (
    <div ref={externalRef} className="sidebar tasks-sidebar">
      {/* Project Selection */}
      <div className="project-container">
        <span>Current Project: {currentProject ? currentProject.name : "Select Project"}</span>
        <br></br>
        <button 
          className="project-button"
          onClick={() => setShowProjectManager(true)}
        >
          {"Select Project"}
        </button>
      </div>

      {/* Sidebar Header */}
      <div className="sidebar-header">
        <div style={{ display: 'flex', gap: '5px', marginBottom: '10px' }}>
          <button
            onClick={() => exportTasksToExcel(tasks, currentProject?.name, events, contractors)}
            className="save-btn"
            style={{ flex: 1, fontSize: '12px', padding: '6px' }}
            disabled={!tasks.length}
          >
            Export Excel
          </button>
        </div>
        {(canAddTasks || isAdmin) && (
          <div style={{ display: 'flex', gap: '5px', marginBottom: '10px' }}>
            <button
              onClick={() => setShowAddTaskModal(true)}
              className="add-contractor-btn"
              style={{ flex: 1, fontSize: '12px', padding: '6px' }}
              disabled={!currentProject}
            >
              + Add Task
            </button>
          </div>
        )}
        <h4 className="sidebar-title">Construction Tasks</h4>

        {/* Search Box */}
        <div className="search-box" style={{ marginBottom: '10px', position: 'relative' }}>
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-input"
            style={{ fontSize: '13px', padding: '6px 8px' }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                color: '#666',
                padding: '0 4px'
              }}
            >
              ×
            </button>
          )}
        </div>

        {/* Phase Filter Dropdown */}
        <div className="phase-filter">
          <label htmlFor="phaseFilter" className="filter-label">
            Filter by Phase:
          </label>
          <select
            id="phaseFilter"
            value={phaseFilter}
            onChange={(e) => setPhaseFilter(e.target.value)}
            className="filter-select"
          >
            <option value="All">All Phases</option>
            {Object.keys(groupedTasks).map((phase, i) => (
              <option key={i} value={phase}>{phase}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="sidebar-content">
        {loading && <div className="loading-text">Loading tasks...</div>}
        {error && <div className="error-text">Error: {error}</div>}

        {!loading && !error && filteredTasks.length === 0 && searchQuery && (
          <div className="empty-state">
            No tasks found matching "{searchQuery}"
          </div>
        )}

        {!loading && !error && tasks.length === 0 && !searchQuery && (
          <div className="empty-state">
            No construction tasks found. Add some tasks to your project.
          </div>
        )}

        {!loading && !error && Object.keys(groupedTasks).length > 0 &&
          Object.keys(groupedTasks).map(phase => {
            if (phaseFilter !== "All" && phaseFilter !== phase) return null;

            return (
              <div key={phase} className="phase-group">
                <h5 className="phase-title">{phase}</h5>
                {groupedTasks[phase].map((task, i) => {
                  const assignedContractor = contractors.find(c => c.id === task.contractor);
                  const isOutOfOffice = task.title.toLowerCase().includes('out of office');
                  
                  return (
                    <div
                      key={task.id || i}
                      className={`task-item ${
                        task.completed 
                          ? "completed" 
                          : isOutOfOffice 
                            ? "out-of-office"
                            : (assignedContractor ? "assigned" : "pending")
                      }`}
                    >
                      <div className="fc-event task-title">
                        {task.title}
                        {task.completed && " ✓"}
                      </div>
                      {assignedContractor ? (
                        <div 
                          className="contractor-assigned"
                          onClick={() => setSelectedTask(task)}
                        >
                          👤 {assignedContractor.name}
                        </div>
                      ) : (
                        <div 
                          className="contractor-unassigned"
                          onClick={() => setSelectedTask(task)}
                        >
                          Click to assign contractor
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })
        }
      </div>

      {/* Add Task Modal */}
      {showAddTaskModal && (
        <div className="modal-backdrop">
          <div className="modal-container add-contractor-modal">
            <h3 className="modal-title">Add New Task</h3>

            {formError && (
              <div className="form-error">
                {formError}
              </div>
            )}

            <form onSubmit={handleAddTaskSubmit}>
              <div className="form-group">
                <label className="form-label" htmlFor="title">
                  Task Title <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleAddTaskChange}
                  className="form-input"
                  placeholder="Enter task title..."
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="phase">
                  Phase <span className="required">*</span>
                </label>
                <select
                  id="phase"
                  name="phase"
                  value={formData.phase}
                  onChange={handleAddTaskChange}
                  className="form-select"
                  required
                >
                  <option value="">Select a phase...</option>
                  {allPhases.map((phase, index) => (
                    <option key={index} value={phase}>{phase}</option>
                  ))}
                  <option value="Other">Other</option>
                </select>
              </div>

              {showAddTaskOptions && (
                <div className="form-group">
                  <label className="form-label" htmlFor="addTo">
                    Add Task To <span className="required">*</span>
                  </label>
                  <select
                    id="addTo"
                    name="addTo"
                    value={formData.addTo}
                    onChange={handleAddTaskChange}
                    className="form-select"
                    required
                  >
                    <option value="current">Current Project Only</option>
                    <option value="all">All Existing Projects</option>
                    <option value="template">Task Template (Future Projects)</option>
                  </select>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    {formData.addTo === 'current' && 'Task will be added to the current project only'}
                    {formData.addTo === 'all' && 'Task will be added to all existing projects'}
                    {formData.addTo === 'template' && 'Task will be added to the template and appear in all new projects'}
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddTaskModal(false);
                    setFormData({ title: '', phase: '', addTo: 'current' });
                    setFormError('');
                  }}
                  className="modal-cancel-btn"
                  disabled={addTaskLoading}
                >
                  Cancel
                </button>

                <button 
                  type="submit" 
                  className="save-btn" 
                  disabled={addTaskLoading}
                >
                  {addTaskLoading ? 'Adding...' : 'Add Task'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Project Manager Modal */}
      {showProjectManager && (
        <ProjectManager
          currentProject={currentProject}
          projects={projects}
          onSelectProject={setCurrentProjectId}
          onCreateProject={createProject}
          onDeleteProject={deleteProject}
          setShowProjectManager={setShowProjectManager}
        />
      )}
    </div>
  );
}

export default TasksSidebar;